import React, { useState } from 'react';
import { usePortletStore } from '../App';
import {
  Breakpoints,
  intl,
  Maskable,
  showError,
  TextInput,
  Tool,
  useBreakpoint,
  useManagedForm,
  yup
} from 'ehs-react';
import {
  EmailField,
  emailValidationSchema,
  getYupSchema,
  mobilePhoneValidationSchema,
  PhoneField
} from './CompleteUserAccountPage';
import { submitUserCreationData } from '../stores/ajax';
import { SecondaryBackButton } from './SecondaryBackButton';
import type { at } from '../generated/Beans';

type MinimalUserData = at.sense.portlets.saf.patientselfonboarding.model.ui.MinimalUserData;

export function CreateUserAccountPage({
  onAccountCreated,
  onBackClicked
}: Readonly<{
  onAccountCreated: () => void;
  onBackClicked: () => void;
}>): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const [
    emailValidationConfiguration,
    phoneNumberValidationConfiguration,
    phoneNumberInputMandatory,
    apiRequestUrl,
    getAgreementsToSubmit,
    userData,
    setUserData
  ] = usePortletStore(state => [
    state.configuration.emailValidationConfiguration,
    state.configuration.phoneValidationConfiguration,
    state.configuration.phoneNumberInputMandatory,
    state.url,
    state.getAgreementsToSubmit,
    state.dataForCreateUser,
    state.setDataForCreateUser
  ]);

  const getPatternForAtLeastOneNonWhitespaceCharacter = () => {
    return '^(.*\\S.*)$';
  };

  const managedForm = useManagedForm<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  }>({
    validateOnMount: true,
    initialValues: userData,
    validationSchema: yup
      .object()
      .shape({
        firstName: getYupSchema(true).matches(
          new RegExp(getPatternForAtLeastOneNonWhitespaceCharacter())
        ),
        lastName: getYupSchema(true).matches(
          new RegExp(getPatternForAtLeastOneNonWhitespaceCharacter())
        ),
        email: emailValidationSchema(emailValidationConfiguration),
        phoneNumber: mobilePhoneValidationSchema(
          phoneNumberValidationConfiguration,
          phoneNumberInputMandatory
        )
      })
      .defined(),
    onSubmit(values): void | Promise<any> {
      const submitData: MinimalUserData = {
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        email: values.email || '',
        phoneNumber: values.phoneNumber || '',
        selectedAgreements: getAgreementsToSubmit()
      };
      setUserData(submitData);

      return submitUserCreationData({
        url: apiRequestUrl,
        userData: submitData,
        setLoading: visible => setLoading(visible)
      })
        .then(onAccountCreated)
        .catch(showError);
    }
  });

  const isMobileView = useBreakpoint(Breakpoints.MOBILE);

  return (
    <Maskable masked={loading} className="welcome-load-mask">
      <div className="complete-account-container s-font">
        <div className="complete-account-header">
          {
            <>
              {isMobileView && (
                <Tool
                  icon="arrow-left"
                  onClick={onBackClicked}
                  tooltip={intl('view.button.back')}
                />
              )}
            </>
          }
          <h1 className="complete-account-header-text font-18 center sh-font">
            {intl('patientSelfOnboardingPortlet.createAccount.title')}
          </h1>
        </div>
        <div className="complete-account-content">
          <div className="complete-account-content-description">
            <div className="complete-account-image-container">
              <img
                alt=""
                className="contact-validation-image"
                src="/o/PatientSelfOnboardingPortlet/images/complete_user_account.svg"
                role="none"
              />
            </div>
            <div>
              <h2 className="complete-account-header-text sh-font font-22">
                {intl('patientSelfOnboardingPortlet.createAccount.input.title')}
              </h2>
              <p className="complete-account-description-text font-16">
                {intl(
                  'patientSelfOnboardingPortlet.createAccount.input.description'
                )}
              </p>
            </div>
          </div>
          <form
            {...managedForm.getFormProps()}
            title={intl('patientSelfOnboardingPortlet.createAccount.title')}
          >
            <div className="input-form-fields">
              <div className="input-first-name-container">
                <TextInput
                  required={true}
                  label={intl('common.name.given')}
                  type="text"
                  {...managedForm.getField('firstName')}
                  error={
                    !managedForm.isSubmitting &&
                    managedForm.touched.firstName &&
                    managedForm.errors.firstName
                      ? intl('view.field.mandatory')
                      : ''
                  }
                />
              </div>
              <div className="input-last-name-container">
                <TextInput
                  required={true}
                  label={intl('common.name.family')}
                  type="text"
                  {...managedForm.getField('lastName')}
                  error={
                    !managedForm.isSubmitting &&
                    managedForm.touched.lastName &&
                    managedForm.errors.lastName
                      ? intl('view.field.mandatory')
                      : ''
                  }
                />
              </div>

              <EmailField
                managedForm={managedForm}
                example={emailValidationConfiguration.examples}
              />
              <PhoneField
                managedForm={managedForm}
                example={phoneNumberValidationConfiguration.examples}
                phoneNumberInputMandatory={phoneNumberInputMandatory}
              />
              <div className="content-button-bar">
                {managedForm.getSubmitButton({
                  disableIfNotValid: true,
                  buttonScale: 'medium',
                  text: intl(
                    'patientSelfOnboardingPortlet.welcomeScreen.buttonStartNow'
                  )
                })}
              </div>
              {!isMobileView && (
                <SecondaryBackButton onBackClicked={onBackClicked} />
              )}
            </div>
          </form>
        </div>
      </div>
    </Maskable>
  );
}
